import React, { useEffect } from 'react';
import Panel from './panels/Panel';
import _find from 'lodash/find';
import styled from '@emotion/styled';
import { Select } from './Select';
import TextInput from './TextInput';
import Button from './buttons/Button';
import { isDarkMode, useFilteredBodyType } from '../utils/helpers';
import CheckboxGroup from '../atoms/CheckboxGroup';
import { getInputTheme } from '../style';
import ToggleButton from './togglebutton/ToggleButton';
import CancelButton from './buttons/ProfileCancelButton';
import { ANNUAL_INCOME_OPTIONS, NET_WORTH_OPTIONS } from '../components/join/constants';
import { OCCUPATION_MAX_CHARACTER } from '../config/constants';
import { DashlineSeparator } from 'components/join/styledElements';

const ButtonContainer = styled('div')({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  marginBottom: 15,
});

const toggleBtnLabelStyle = {
  padding: '0',
  color: isDarkMode() ? '#71B5E4' : '#2B8FD7',
};

const customToggle = {
  padding: '8px 18px',
  height: 14,
  border: '1px solid #71B5E4',
  backgroundColor: 'transparent',
};

const customToggleSelected = {
  padding: '8px 18px',
  height: 14,
  backgroundColor: '#2B8FD7',
  border: '1px solid #2B8FD7',
};

const checkTabletWidth = screen.width >= 768 && screen.width <= 820;

const ToggleButtonsContainer = styled('div')({
  width: '100%',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gridGap: '12px 25px',
  ['@media (min-width: 768px) and (max-width: 820px) ']: {
    gridTemplateColumns: '1fr 1fr 1fr',
  },
});

const ToggleContainer = styled('div')({
  width: '100%',
  display: 'flex',
  gap: 12,
});

const errorPropEvaluator = (error, output) => {
  return error && output;
};

interface EditablePersonalInfoProps {
  errors: any;
  bodyTypes: any;
  height: any;
  totalChildren: any;
  smoking: any;
  drinking: any;
  education: any;
  occupation: any;
  income: any;
  lookingMale: any;
  lookingFemale: any;
  onChangeHandler: any;
  mapFields: any;
  title: any;
  className: any;
  handleCancel: any;
  handleSubmit: any;
  onChangeBodyType: any;
  onCheckboxChangeHandler: any;
  onToggleChildren: any;
  isGenerous: any;
  gender: string;
  errorCount?: number;
  net_worth: number;
  [rest: string]: any;
}

const EditablePersonalInfo = ({
  errors,
  bodyTypes,
  height,
  totalChildren,
  smoking,
  drinking,
  education,
  occupation,
  income,
  lookingMale,
  lookingFemale,
  onChangeHandler,
  mapFields,
  title,
  className,
  handleCancel,
  handleSubmit,
  onChangeBodyType,
  onCheckboxChangeHandler,
  onToggleChildren,
  isGenerous,
  gender,
  net_worth,
  ...rest
}: EditablePersonalInfoProps): JSX.Element => {
  const filteredBodyType = useFilteredBodyType(gender);

  const renderEditField = (field, disabled = true) => {
    if (field.findIndex(data => data.value === 0) === -1) {
      field.unshift({ value: 0, label: '—' });
    }

    return field.map(data => {
      return (
        <option key={data.value} value={data.value} disabled={data.value === '0' && disabled}>
          {data.label}
        </option>
      );
    });
  };

  const handleDisableOptions = () => {
    return bodyTypes.length > 0;
  };

  useEffect(() => {
    const bodyTypeRef = document.getElementById('bodyTypeRef');
    const bodyTypesSelectInput = document.getElementsByClassName('select__input')[0];

    if (bodyTypeRef) bodyTypeRef.style.color = '#000000';

    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    if (bodyTypesSelectInput) bodyTypesSelectInput.style.color = getInputTheme().colorInput;

    if (checkTabletWidth && bodyTypeRef) bodyTypeRef.style.width = '735px';
  }, []);

  const setCustomStyle = item => {
    return item ? customToggleSelected : customToggle;
  };

  const setCustomLabelStyle = item => {
    return item ? { ...toggleBtnLabelStyle, color: '#FFFFFF' } : toggleBtnLabelStyle;
  };

  return (
    <Panel
      data-test-id="editable-about-info"
      withHeader
      title={title}
      className={className}
      cancelButton={
        <CancelButton
          dataTestId="editable-textarea-cancel-btn"
          handleCancel={handleCancel}
          customStyle={{ marginRight: 0 }}
          isTextType
        />
      }
      panelStyle="borderless"
      {...rest}
    >
      <CheckboxGroup
        inputLabel="I’m Interested In"
        status={errorPropEvaluator(errors.interested, 'error')}
        statusMessage={errors.interested}
        customStyle={errors.interested ? { paddingBottom: 0, color: '#CC5453' } : { paddingBottom: 0 }}
        customLabelStyle={{
          fontSize: 15,
          lineHeight: '20px',
          letterSpacing: -0.2,
          color: isDarkMode() ? '#F6F4F4' : '#595959',
        }}
        customContainerStyle={{ marginBottom: 10 }}
        isRequired
        noStatusIcon
      >
        <ToggleContainer>
          <ToggleButton
            customStyle={{ ...setCustomStyle(lookingMale), margin: 0 }}
            labelStyle={setCustomLabelStyle(lookingMale)}
            dataTestId="looking_male"
            hideIcons
            title="Men"
            id="looking_male"
            isChecked={lookingMale}
            onChangeHandler={() => onCheckboxChangeHandler('looking_male')}
          />
          <ToggleButton
            customStyle={{ ...setCustomStyle(lookingFemale), margin: 0 }}
            labelStyle={setCustomLabelStyle(lookingFemale)}
            dataTestId="looking_female"
            hideIcons
            title="Women"
            id="looking_female"
            isChecked={lookingFemale}
            onChangeHandler={() => onCheckboxChangeHandler('looking_female')}
          />
        </ToggleContainer>
      </CheckboxGroup>
      <DashlineSeparator style={{ margin: '20px 0' }} />
      <CheckboxGroup
        inputLabel="Body Type"
        subLabel={'(Choose one)'}
        inlineSubLabel={true}
        customSubLabelStyle={{
          lineHeight: '16px',
          letterSpacing: -0.3,
          color: isDarkMode() ? '#F6F4F4' : '#595959',
          fontStyle: 'unset',
        }}
        status={errorPropEvaluator(errors.body_type, 'error')}
        statusMessage={errors.body_type}
        customStyle={{ paddingBottom: 0 }}
        customErrorMessageStyle={{
          position: 'unset',
          marginTop: 4,
        }}
        customLabelStyle={{
          fontWeight: 600,
          fontSize: 15,
          lineHeight: '16px',
          letterSpacing: -0.3,
          color: isDarkMode() ? '#F6F4F4' : '#595959',
        }}
        customCheckboxListStyle={{ marginTop: 10 }}
        isRequired
        noStatusIcon
      >
        <ToggleButtonsContainer>
          {filteredBodyType.map((item, index) => (
            <ToggleButton
              dataTestId={item.value}
              key={index}
              title={item.value === 5 ? 'A Few Extra' : item.label}
              id={item.value}
              isChecked={_find(bodyTypes, item)}
              isDisabled={handleDisableOptions() && !_find(bodyTypes, item)}
              onChangeHandler={
                handleDisableOptions() && !_find(bodyTypes, item)
                  ? () => null
                  : () => onChangeBodyType(item, 'body_type')
              }
              customStyle={{ margin: 0 }}
            />
          ))}
        </ToggleButtonsContainer>
      </CheckboxGroup>
      <DashlineSeparator style={{ margin: '20px 0' }} />
      <Select
        value={height || '0'}
        id="height"
        inputLabel="Height"
        name="height"
        onChange={onChangeHandler}
        error={errors.height}
        status={errorPropEvaluator(errors.height, 'error')}
        statusMessage={errors.height}
        style={{
          width: '100%',
          maxWidth: '711px',
          lineHeight: '19px',
          letterSpacing: -0.3,
          color: getInputTheme().textInput,
        }}
        baseSelectStyle={{ marginRight: 0, paddingBottom: errors.height ? 24 : 0 }}
        customStyleLabel={{
          fontWeight: 600,
          fontSize: 15,
          lineHeight: '16px',
          color: isDarkMode() ? '#F6F4F4' : '#595959',
        }}
        customContainerStyle={{ marginBottom: 10 }}
        isRequired
        noStatusIcon
      >
        <option value={'0'} disabled>
          —
        </option>
        <option value={`< 5'0" (<152cm)`}>{`< 5'0" (<152cm)`}</option>
        <option value={`5'0" (152cm)`}>{`5'0" (152cm)`}</option>
        <option value={`5'1" (155cm)`}>{`5'1" (155cm)`}</option>
        <option value={`5'2" (157cm)`}>{`5'2" (157cm)`}</option>
        <option value={`5'3" (160cm)`}>{`5'3" (160cm)`}</option>
        <option value={`5'4" (163cm)`}>{`5'4" (163cm)`}</option>
        <option value={`5'5" (165cm)`}>{`5'5" (165cm)`}</option>
        <option value={`5'6" (168cm)`}>{`5'6" (168cm)`}</option>
        <option value={`5'7" (170cm)`}>{`5'7" (170cm)`}</option>
        <option value={`5'8" (173cm)`}>{`5'8" (173cm)`}</option>
        <option value={`5'9" (175cm)`}>{`5'9" (175cm)`}</option>
        <option value={`5'10" (178cm)`}>{`5'10" (178cm)`}</option>
        <option value={`5'11" (180cm)`}>{`5'11" (180cm)`}</option>
        <option value={`6'0" (183cm)`}>{`6'0" (183cm)`}</option>
        <option value={`6'1" (185cm)`}>{`6'1" (185cm)`}</option>
        <option value={`6'2" (188cm)`}>{`6'2" (188cm)`}</option>
        <option value={`6'3" (191cm)`}>{`6'3" (191cm)`}</option>
        <option value={`6'4" (193cm)`}>{`6'4" (193cm)`}</option>
        <option value={`6'5" (196cm)`}>{`6'5" (196cm)`}</option>
        <option value={`6'6" (198cm)`}>{`6'6" (198cm)`}</option>
        <option value={`6'7" (201cm)`}>{`6'7" (201cm)`}</option>
        <option value={`6'8" (203cm)`}>{`6'8" (203cm)`}</option>
        <option value={`6'9" (206cm)`}>{`6'9" (206cm)`}</option>
        <option value={`6'10" (208cm)`}>{`6'10" (208cm)`}</option>
        <option value={`6'11" (211cm)`}>{`6'11" (211cm)`}</option>
        <option value={`7'0" (214cm)`}>{`7'0" (214cm)`}</option>
        <option value={`> 7'0" (> 214cm)`}>{`> 7'0" (> 214cm)`}</option>
      </Select>
      <DashlineSeparator style={{ margin: '20px 0' }} />
      <CheckboxGroup
        inputLabel="Children"
        status={errorPropEvaluator(errors.children, 'error')}
        statusMessage={errors.children}
        customStyle={{ paddingBottom: 0 }}
        customErrorMessageStyle={{
          position: 'unset',
          marginTop: 4,
        }}
        customLabelStyle={{
          fontWeight: 600,
          fontSize: 15,
          lineHeight: '20px',
          letterSpacing: -0.3,
          color: isDarkMode() ? '#F6F4F4' : '#595959',
        }}
        isRequired
        noStatusIcon
      >
        <ToggleContainer>
          {Object.keys(mapFields.children).map((key, index) => {
            const isSelected = key === totalChildren.toString();

            return (
              index !== 0 && (
                <ToggleButton
                  customStyle={{ ...setCustomStyle(isSelected), margin: 0 }}
                  labelStyle={setCustomLabelStyle(isSelected)}
                  radioBehaviour
                  dataTestId={mapFields.children[key]}
                  hideIcons
                  key={index}
                  title={mapFields.children[key]}
                  id={mapFields.children[key]}
                  isChecked={isSelected}
                  onChangeHandler={() => onToggleChildren('children', key)}
                />
              )
            );
          })}
        </ToggleContainer>
      </CheckboxGroup>
      <DashlineSeparator style={{ margin: '20px 0' }} />
      <CheckboxGroup
        inputLabel="Smoking"
        status={errorPropEvaluator(errors.smoking, 'error')}
        statusMessage={errors.smoking}
        customStyle={{ paddingBottom: 0 }}
        customErrorMessageStyle={{
          position: 'unset',
          marginTop: 4,
        }}
        customLabelStyle={{
          fontWeight: 600,
          fontSize: 15,
          lineHeight: '20px',
          letterSpacing: -0.3,
          color: isDarkMode() ? '#F6F4F4' : '#595959',
        }}
        isRequired
        noStatusIcon
      >
        <ToggleContainer>
          {Object.keys(mapFields.smoking).map((key, index) => {
            const isSelected = key === smoking.toString();

            return (
              index !== 0 && (
                <ToggleButton
                  customStyle={{ ...setCustomStyle(isSelected), margin: 0 }}
                  labelStyle={setCustomLabelStyle(isSelected)}
                  radioBehaviour
                  dataTestId={mapFields.smoking[key]}
                  hideIcons
                  key={index}
                  title={mapFields.smoking[key]}
                  id={mapFields.smoking[key]}
                  isChecked={isSelected}
                  onChangeHandler={() => onToggleChildren('smoking', key)}
                />
              )
            );
          })}
        </ToggleContainer>
      </CheckboxGroup>
      <DashlineSeparator style={{ margin: '20px 0' }} />
      <CheckboxGroup
        inputLabel="Drinking"
        status={errorPropEvaluator(errors.drinking, 'error')}
        statusMessage={errors.drinking}
        customErrorMessageStyle={{
          position: 'unset',
          marginTop: 4,
        }}
        customStyle={{ paddingBottom: 0 }}
        customLabelStyle={{
          fontWeight: 600,
          fontSize: 15,
          lineHeight: '20px',
          letterSpacing: -0.3,
          color: isDarkMode() ? '#F6F4F4' : '#595959',
        }}
        isRequired
        noStatusIcon
      >
        <ToggleContainer>
          {Object.keys(mapFields.drinking).map((key, index) => {
            const isSelected = key === drinking.toString();

            return (
              index !== 0 && (
                <ToggleButton
                  customStyle={{ ...setCustomStyle(isSelected), margin: 0 }}
                  labelStyle={setCustomLabelStyle(isSelected)}
                  radioBehaviour
                  dataTestId={mapFields.drinking[key]}
                  hideIcons
                  key={index}
                  title={mapFields.drinking[key]}
                  id={mapFields.drinking[key]}
                  isChecked={isSelected}
                  onChangeHandler={() => onToggleChildren('drinking', key)}
                />
              )
            );
          })}
        </ToggleContainer>
      </CheckboxGroup>
      <DashlineSeparator style={{ margin: '20px 0' }} />
      <Select
        value={education || '0'}
        id="education"
        inputLabel="Education"
        name="education"
        onChange={onChangeHandler}
        status={errorPropEvaluator(errors.education, 'error')}
        statusMessage={errors.education}
        style={{
          width: '100%',
          maxWidth: '711px',
          lineHeight: '19px',
          letterSpacing: -0.3,
          color: getInputTheme().textInput,
        }}
        baseSelectStyle={{ marginRight: 0, paddingBottom: errors.education ? 24 : 0 }}
        customStyleLabel={{
          fontWeight: 600,
          fontSize: 15,
          lineHeight: '20px',
          letterSpacing: -0.3,
          color: isDarkMode() ? '#F6F4F4' : '#595959',
        }}
        customContainerStyle={{ marginBottom: 10 }}
        isRequired
        noStatusIcon
      >
        <option value={'0'} disabled>
          —
        </option>
        <option value={'High School'}>High school</option>
        <option value={'Some College'}>Some college</option>
        <option value={'Associates Degree'}>Associate degree</option>
        <option value={"Bachelor's Degree"}>Bachelor&apos;s degree</option>
        <option value={'Graduate Degree'}>Graduate degree</option>
        <option value={'PhD / Post Doctoral'}>Post-doctoral degree</option>
      </Select>
      <DashlineSeparator style={{ margin: '20px 0' }} />
      <TextInput
        id="occupation"
        inputLabel="Occupation"
        name="occupation"
        value={occupation || ''}
        onChange={onChangeHandler}
        status={
          occupation?.length > OCCUPATION_MAX_CHARACTER ? 'error' : errorPropEvaluator(errors.occupation, 'error')
        }
        statusMessage={errors.occupation}
        customStyle={{ width: '100%', maxWidth: '711px', paddingBottom: 0 }}
        hideStatusIcon
        isRequired={isGenerous}
        customStyleLabel={{
          fontWeight: 600,
          fontSize: 15,
          lineHeight: '20px',
          letterSpacing: -0.3,
          color: isDarkMode() ? '#F6F4F4' : '#595959',
        }}
        showCounter={true}
        maxCount={OCCUPATION_MAX_CHARACTER}
        placeholder="Type your Occupation here..."
        customInputStyle={{
          fontSize: 14,
          lineHeight: '19px',
        }}
      />
      <DashlineSeparator style={{ margin: '20px 0' }} />
      {isGenerous && (
        <>
          <Select
            value={income}
            id="income"
            name="income"
            inputLabel="Income"
            onChange={onChangeHandler}
            status={errorPropEvaluator(errors.income, 'error')}
            statusMessage={errors.income}
            style={{
              width: '100%',
              maxWidth: '711px',
              lineHeight: '19px',
              letterSpacing: -0.3,
              color: getInputTheme().textInput,
            }}
            baseSelectStyle={{ marginRight: 0, paddingBottom: errors.income ? 24 : 0 }}
            customStyleLabel={{
              fontWeight: 600,
              fontSize: 15,
              lineHeight: '20px',
              letterSpacing: -0.3,
              color: isDarkMode() ? '#F6F4F4' : '#595959',
            }}
            customContainerStyle={{ marginBottom: 10 }}
            isRequired={true}
            noStatusIcon
          >
            {!isNaN(income)
              ? renderEditField(ANNUAL_INCOME_OPTIONS, false)
              : [
                  <option key={0}>Prefer Not To Say</option>,
                  <option key={1}>Less than US$20,000</option>,
                  <option key={2}>US$20,001 - $30,000</option>,
                  <option key={3}>US$30,001 - $40,000</option>,
                  <option key={4}>US$40,001 - $50,000</option>,
                  <option key={5}>US$50,001 - $60,000</option>,
                  <option key={6}>US$60,001 - $70,000</option>,
                  <option key={7}>US$70,001 - $80,000</option>,
                  <option key={8}>US$80,001 - $100,000</option>,
                  <option key={9}>US$100,001 - $150,000</option>,
                  <option key={10}>US$150,001 - $200,000</option>,
                  <option key={11}>US$200,001 - $300,000</option>,
                  <option key={12}>US$300,001 - $400,000</option>,
                  <option key={13}>US$400,001 - $500,000</option>,
                  <option key={14}>US$500,001 - $1 million</option>,
                  <option key={15}>More than $1 million</option>,
                ]}
          </Select>
          <DashlineSeparator style={{ margin: '20px 0' }} />
        </>
      )}
      {isGenerous && (
        <>
          <Select
            value={net_worth}
            id="net_worth"
            name="net_worth"
            inputLabel="Net Worth"
            onChange={onChangeHandler}
            status={errorPropEvaluator(errors.net_worth, 'error')}
            statusMessage={errors.net_worth}
            style={{
              width: '100%',
              maxWidth: '711px',
              lineHeight: '19px',
              letterSpacing: -0.3,
              color: getInputTheme().textInput,
            }}
            baseSelectStyle={{ marginRight: 0, paddingBottom: errors.net_worth ? 24 : 0 }}
            customStyleLabel={{
              fontWeight: 600,
              fontSize: 15,
              lineHeight: '20px',
              letterSpacing: -0.3,
              color: isDarkMode() ? '#F6F4F4' : '#595959',
            }}
            customContainerStyle={{ marginBottom: 10 }}
            isRequired={true}
            noStatusIcon
          >
            {renderEditField(NET_WORTH_OPTIONS, false)}
          </Select>
          <DashlineSeparator style={{ margin: '20px 0' }} />
        </>
      )}

      <ButtonContainer>
        <Button
          isDefault={true}
          onClick={handleCancel}
          buttonType="secondaryBlue"
          customStyle={{
            border: '1px solid #60ABE1',
            padding: '8px 18px',
            marginRight: 15,
            '&:hover': {
              background: isDarkMode() ? 'transparent !important' : 'inherit',
            },
            fontWeight: '600 !important',
            fontSize: '13px !important',
            lineHeight: '16px',
            borderRadius: '4px',
          }}
        >
          Cancel
        </Button>
        <Button
          data-test-id="save-about-info"
          customStyle={{
            padding: '8px 19px',
            fontWeight: 600,
            fontSize: 13,
            lineHeight: '16px',
            borderRadius: '4px !important',
          }}
          buttonType="primaryRed"
          onClick={e => handleSubmit(e)}
        >
          Save
        </Button>
      </ButtonContainer>
    </Panel>
  );
};

export default EditablePersonalInfo;
